/**
 * Genel bakış ekranı.
 *
 * Amaç tek soruya cevap vermek: **"şu an müdahale gerektiren bir şey var mı?"**
 * Bu yüzden en üstte "dikkat isteyen" sayacı durur ve sıfırdan büyükse ekranın
 * en yüksek görsel şiddetini alır. Diğer sayılar bilgilendirmedir.
 *
 * Büyük sayı kutuları yalnızca bu ekranda var (docs/13: "her şey kart değildir"):
 * burada sayının kendisi ekranın konusudur.
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { ArrowRight, CircleAlert, Clock, FileCheck2, ListChecks, Wallet } from 'lucide-react';
import { api, type Ozet } from '../lib/api';
import { Kabuk } from '../components/Kabuk';
import { Kart, KartBasligi, KartGovdesi } from '../components/ui/Kart';
import { Olcum, OlcumSatiri } from '../components/ui/Olcum';
import { HataKutusu, Yukleniyor } from '../components/Durumlar';
import { Dugme } from '../components/ui/Dugme';
import { paraYaz } from '../lib/format';
import { cn } from '../lib/cn';
import { useOturum } from '../oturum';
import { izinVar } from '../lib/yetki';
import { engelSayisi, uyariSayisi } from '../lib/kurulum';

/** Durum sırası: önce dikkat isteyenler, sonra normal akış. */
const DURUM_SIRASI = [
  'INVALID',
  'FAILED',
  'PENDING',
  'SENT',
  'GIB_REPORTED',
  'DELIVERED',
  'CANCELLED',
  'REFUNDED',
] as const;

const DURUM_ETIKETI: Record<string, string> = {
  PENDING: 'Sırada',
  SENT: 'Kesildi',
  GIB_REPORTED: 'GİB’e raporlandı',
  DELIVERED: 'Alıcıya ulaştı',
  CANCELLED: 'İptal edildi',
  REFUNDED: 'İade edildi',
  INVALID: 'Hatalı',
  FAILED: 'Gönderilemedi',
};

function DurumDagilimi({ ozet }: { ozet: Ozet }) {
  const toplam = DURUM_SIRASI.reduce((top, durum) => top + (ozet.counts[durum] ?? 0), 0);

  if (toplam === 0) {
    return (
      <KartGovdesi>
        <p className="text-sm text-[var(--ink-muted)]">
          Henüz fatura yok. İlk Shopify siparişi ödendiğinde fatura otomatik kesilir ve burada görünür.
        </p>
      </KartGovdesi>
    );
  }

  return (
    <KartGovdesi className="flex flex-col gap-3">
      {DURUM_SIRASI.filter((durum) => (ozet.counts[durum] ?? 0) > 0).map((durum) => {
        const sayi = ozet.counts[durum] ?? 0;
        const oran = Math.round((sayi / toplam) * 100);
        const dikkat = durum === 'INVALID' || durum === 'FAILED';
        return (
          <Link
            key={durum}
            to={`/faturalar?status=${durum}`}
            className="group flex flex-col gap-1 rounded-[var(--radius-md)] p-2 hover:bg-[var(--surface-hover)]"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-medium text-[var(--ink)]">{DURUM_ETIKETI[durum] ?? durum}</span>
              <span className="rakam text-sm text-[var(--ink-muted)]">
                {sayi} <span className="text-[var(--ink-faint)]">· %{oran}</span>
              </span>
            </div>
            {/* Oran çubuğu: sayının yanında büyüklük duygusu verir. */}
            <div className="h-1.5 overflow-hidden rounded-full bg-[var(--surface-sunken)]">
              <div
                className={cn('h-full rounded-full', dikkat ? 'bg-[var(--state-bad)]' : 'bg-[var(--accent)]')}
                style={{ width: `${Math.max(oran, 2)}%` }}
              />
            </div>
          </Link>
        );
      })}
    </KartGovdesi>
  );
}

function KuyrukDurumu({ ozet }: { ozet: Ozet }) {
  const girisler = Object.entries(ozet.queue).filter(([, sayi]) => sayi > 0);

  return (
    <KartGovdesi>
      {girisler.length === 0 ? (
        <p className="text-sm text-[var(--ink-muted)]">
          Kuyruk boş — bekleyen iş yok. Yeni sipariş geldiğinde iş otomatik oluşur.
        </p>
      ) : (
        <dl className="flex flex-col gap-2">
          {girisler.map(([durum, sayi]) => (
            <div
              key={durum}
              className="flex items-baseline justify-between gap-3 border-b border-[var(--line)] pb-2 last:border-0 last:pb-0"
            >
              <dt className="text-sm text-[var(--ink-muted)]">{kuyrukEtiketi(durum)}</dt>
              <dd className="rakam text-sm font-medium text-[var(--ink)]">{sayi}</dd>
            </div>
          ))}
        </dl>
      )}
    </KartGovdesi>
  );
}

function kuyrukEtiketi(durum: string): string {
  const etiketler: Record<string, string> = {
    QUEUED: 'Sırada bekleyen',
    RUNNING: 'Şu an çalışan',
    FAILED: 'Başarısız (tekrar denenecek)',
    DEAD: 'Vazgeçilen — insan bakmalı',
    DONE: 'Tamamlanan',
  };
  return etiketler[durum] ?? durum;
}

/**
 * Kurulum eksikse pano bunu SÖYLER — "neden fatura kesilmiyor" sorusu için
 * kimse Kurulum ekranını bilmek zorunda değil. Tam ise hiç çizilmez: "her şey
 * yolunda" kartı gürültüdür.
 */
function KurulumUyarisi() {
  const gecis = useNavigate();
  const { data } = useQuery({ queryKey: ['kurulum'], queryFn: api.kurulum, refetchInterval: 60_000 });
  if (!data) return null;

  const engeller = engelSayisi(data.adimlar);
  const uyarilar = uyariSayisi(data.adimlar);
  if (engeller === 0 && uyarilar === 0) return null;

  const engel = engeller > 0;
  return (
    <Kart className={engel ? 'border border-[var(--state-bad)]' : 'border border-[var(--state-wait)]'}>
      <KartGovdesi className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <ListChecks
            size={20}
            className={cn('mt-0.5 shrink-0', engel ? 'text-[var(--state-bad)]' : 'text-[var(--state-wait)]')}
            aria-hidden="true"
          />
          <p className="max-w-[65ch] text-sm text-[var(--ink)]">
            <strong className="font-semibold">
              {engel
                ? `Kurulum tamamlanmadı — fatura kesilmiyor (${engeller} adım eksik).`
                : `${uyarilar} bağlantı eksik.`}
            </strong>{' '}
            {engel
              ? 'Gelen siparişler kuyrukta bekliyor, hiçbiri kaybolmaz. Eksikler tamamlanınca kaldığı yerden devam eder.'
              : 'Fatura kesilir; ama fatura numarası siparişe yazılmaz ya da mutabakat yapılamaz.'}
          </p>
        </div>
        <Dugme
          gorunum={engel ? 'tehlike' : 'ikincil'}
          boy="sm"
          ikon={<ArrowRight size={13} />}
          onClick={() => void gecis('/kurulum')}
        >
          Kurulumu aç
        </Dugme>
      </KartGovdesi>
    </Kart>
  );
}

/** Kapı kapalıysa (kontör bitti, Veriban oturumu yok) pano bunu ilk satırda söyler. */
function KapiUyarisi({ hazirlik }: { hazirlik: NonNullable<Ozet['hazirlik']> }) {
  const engeller = hazirlik.kontroller.filter((k) => k.durum === 'ENGEL');
  const uyarilar = hazirlik.kontroller.filter((k) => k.durum === 'UYARI');
  if (engeller.length === 0 && uyarilar.length === 0) return null;
  const engel = engeller.length > 0;
  const liste = engel ? engeller : uyarilar;
  return (
    <Kart className={engel ? 'border border-[var(--state-bad)]' : 'border border-[var(--state-wait)]'}>
      <KartGovdesi className="flex items-start gap-3">
        <CircleAlert
          size={20}
          className={cn('mt-0.5 shrink-0', engel ? 'text-[var(--state-bad)]' : 'text-[var(--state-wait)]')}
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-col gap-1 text-sm text-[var(--ink)]">
          <strong className="font-semibold">
            {engel ? 'Fatura kapısı KAPALI — hiçbir iş alınmıyor.' : 'Fatura kapısında uyarı var.'}
          </strong>
          {liste.map((k) => (
            <span key={k.ad}>
              {k.mesaj}
              {k.yapilacak ? ` — ${k.yapilacak}` : ''}
            </span>
          ))}
        </div>
      </KartGovdesi>
    </Kart>
  );
}

export function Panosu() {
  const gecis = useNavigate();
  const { kullanici } = useOturum();
  const { data, error, isPending, refetch } = useQuery({
    queryKey: ['ozet'],
    queryFn: api.ozet,
    // Panel açık kalıp arkada iş işlenirken sayılar kendiliğinden tazelensin.
    refetchInterval: 30_000,
  });

  return (
    <Kabuk
      baslik="Genel bakış"
      altBaslik="Faturaların durumu ve bekleyen işler"
      eylemler={
        <Dugme gorunum="ikincil" boy="sm" onClick={() => void refetch()}>
          Yenile
        </Dugme>
      }
    >
      {error !== null && <HataKutusu hata={error} tekrarDene={() => void refetch()} />}

      {isPending ? (
        <Kart>
          <Yukleniyor satir={4} />
        </Kart>
      ) : data ? (
        <>
          {/*
            Ölçüm satırı — sütun sayısı KABA göre (ekrana değil).
            Kartlar arası boşluk 24px = kart iç dolgusu 24px: iç dolgu kardeş
            boşluğunu geçemez (docs/13 bölüm 2.4).
          */}
          <OlcumSatiri>
            <Olcum
              etiket="Dikkat isteyen"
              deger={String(data.needsAttention)}
              altMetin={
                data.needsAttention > 0
                  ? 'Bu faturalar kesilemedi; incelenmesi gerekiyor.'
                  : 'Müdahale gerektiren fatura yok.'
              }
              ikon={<CircleAlert size={18} strokeWidth={1.75} />}
              ton="kotu"
              vurgulu={data.needsAttention > 0}
              onTikla={
                data.needsAttention > 0 ? () => void gecis('/faturalar?status=INVALID,FAILED') : undefined
              }
            />
            <Olcum
              etiket="Bugün kesilen"
              deger={String(data.today.count)}
              altMetin={`${paraYaz(data.today.total)} ₺`}
              ikon={<FileCheck2 size={18} strokeWidth={1.75} />}
              ton="ok"
            />
            <Olcum
              etiket="Bu ay kesilen"
              deger={String(data.month.count)}
              altMetin={`${paraYaz(data.month.total)} ₺`}
              ikon={<Wallet size={18} strokeWidth={1.75} />}
              ton="bilgi"
            />
            <Olcum
              etiket="Kuyrukta bekleyen"
              deger={String((data.queue['QUEUED'] ?? 0) + (data.queue['RUNNING'] ?? 0))}
              altMetin="Gönderilmek üzere sırada"
              ikon={<Clock size={18} strokeWidth={1.75} />}
              ton="uyari"
            />
          </OlcumSatiri>

          {data.hazirlik && <KapiUyarisi hazirlik={data.hazirlik} />}
          {izinVar(kullanici, 'ayar.goruntule') && <KurulumUyarisi />}

          {data.needsAttention > 0 && (
            <Kart className="border-[var(--state-bad)]">
              <KartGovdesi className="flex flex-wrap items-center justify-between gap-3">
                <p className="max-w-[65ch] text-sm text-[var(--ink)]">
                  <strong className="font-semibold">{data.needsAttention} fatura kesilemedi.</strong>{' '}
                  {data.kesilemeyenSiparis > 0 && (
                    <>
                      {data.kesilemeyenSiparis} tanesi sipariş aşamasında düştü (fatura kaydı yok) —{' '}
                      <Link to="/onay" className="text-[var(--accent)] hover:underline">
                        Onay bekleyen → Kesilemedi
                      </Link>
                      .{' '}
                    </>
                  )}
                  Bunlar Veriban’a gönderilemedi ya da veri hatası nedeniyle reddedildi. Listeden hatanın ne
                  olduğunu görüp düzeltebilirsiniz.
                </p>
                <Dugme
                  gorunum="tehlike"
                  boy="sm"
                  ikon={<ArrowRight size={13} />}
                  onClick={() => void gecis('/faturalar?status=INVALID,FAILED')}
                >
                  Hatalı faturaları göster
                </Dugme>
              </KartGovdesi>
            </Kart>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Kart>
              <KartBasligi baslik="Durum dağılımı" aciklama="Bir duruma tıklayarak o faturaları listeleyin" />
              <DurumDagilimi ozet={data} />
            </Kart>
            <Kart>
              <KartBasligi baslik="İş kuyruğu" aciklama="Arka planda bekleyen ve çalışan işler" />
              <KuyrukDurumu ozet={data} />
            </Kart>
          </div>
        </>
      ) : null}
    </Kabuk>
  );
}
